/**
 * Appearance settings, and the pure logic that resolves them.
 *
 * Kept free of React and of the DOM so the resolution rules can be tested
 * directly. The only function here that touches the document is
 * `applyAppearance`, and it is deliberately the last step rather than woven
 * through the rest.
 */

export const THEME_PREFERENCES = ['light', 'dark', 'auto'] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];

export const ACCENTS = ['azure', 'violet', 'teal', 'emerald', 'amber', 'rose'] as const;
export type AccentName = (typeof ACCENTS)[number];

export const MOTION_PREFERENCES = ['system', 'full', 'reduced', 'off'] as const;
export type MotionPreference = (typeof MOTION_PREFERENCES)[number];

export const CONTRAST_PREFERENCES = ['system', 'normal', 'high'] as const;
export type ContrastPreference = (typeof CONTRAST_PREFERENCES)[number];

/**
 * Text scale as a multiplier on the root font size.
 *
 * Expressed as a scale rather than a pixel size so every space, radius and
 * line height defined in `rem` grows with it. Scaling only the font while
 * boxes stay fixed is how "large text" modes end up with clipped labels.
 */
export const TEXT_SCALES = [0.9, 1, 1.15, 1.3] as const;
export type TextScale = (typeof TEXT_SCALES)[number];

export interface AppearanceSettings {
  readonly theme: ThemePreference;
  readonly accent: AccentName;
  readonly motion: MotionPreference;
  readonly contrast: ContrastPreference;
  readonly textScale: TextScale;
}

export const DEFAULT_APPEARANCE: AppearanceSettings = {
  theme: 'auto',
  accent: 'azure',
  motion: 'system',
  contrast: 'system',
  textScale: 1,
};

/**
 * Storage key.
 *
 * Also read by the inline script in `index.html`, which resolves the theme
 * before first paint to avoid a flash of the wrong colours. If this key or the
 * shape of the stored object changes, that script must change with it.
 */
export const APPEARANCE_STORAGE_KEY = 'cinesync.appearance';

/** What the operating system is currently asking for. */
export interface SystemPreferences {
  readonly prefersDark: boolean;
  readonly prefersReducedMotion: boolean;
  readonly prefersHighContrast: boolean;
}

/** Settings after the system has had its say. This is what the DOM receives. */
export interface ResolvedAppearance {
  readonly theme: 'light' | 'dark';
  readonly accent: AccentName;
  readonly motion: 'full' | 'reduced' | 'off';
  readonly contrast: 'normal' | 'high';
  readonly textScale: TextScale;
}

export function resolveAppearance(
  settings: AppearanceSettings,
  system: SystemPreferences,
): ResolvedAppearance {
  return {
    theme: settings.theme === 'auto' ? (system.prefersDark ? 'dark' : 'light') : settings.theme,
    accent: settings.accent,
    motion:
      settings.motion === 'system'
        ? system.prefersReducedMotion
          ? 'off'
          : 'full'
        : settings.motion,
    contrast:
      settings.contrast === 'system' ? (system.prefersHighContrast ? 'high' : 'normal') : settings.contrast,
    textScale: settings.textScale,
  };
}

/**
 * Read settings from an untrusted blob.
 *
 * Storage is untrusted input like any other. A corrupt or half-written value,
 * or one left by an older version, must degrade to the default rather than
 * crash the application before it has drawn anything. Each field falls back
 * independently, so one bad value does not discard the rest.
 */
export function parseAppearance(raw: unknown): AppearanceSettings {
  let value: unknown = raw;

  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return DEFAULT_APPEARANCE;
    }
  }

  if (typeof value !== 'object' || value === null) return DEFAULT_APPEARANCE;
  const record = value as Record<string, unknown>;

  return {
    theme: pick(record['theme'], THEME_PREFERENCES, DEFAULT_APPEARANCE.theme),
    accent: pick(record['accent'], ACCENTS, DEFAULT_APPEARANCE.accent),
    motion: pick(record['motion'], MOTION_PREFERENCES, DEFAULT_APPEARANCE.motion),
    contrast: pick(record['contrast'], CONTRAST_PREFERENCES, DEFAULT_APPEARANCE.contrast),
    textScale: pick(record['textScale'], TEXT_SCALES, DEFAULT_APPEARANCE.textScale),
  };
}

function pick<T extends string | number>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

/**
 * Write the resolved appearance onto an element, normally `<html>`.
 *
 * The CSS in `tokens.css` selects entirely on these attributes, so this one
 * function is the whole bridge between settings and appearance. Nothing else
 * in the application sets a colour based on the theme.
 */
export function applyAppearance(root: HTMLElement, resolved: ResolvedAppearance): void {
  root.dataset['theme'] = resolved.theme;
  root.dataset['accent'] = resolved.accent;
  root.dataset['motion'] = resolved.motion;
  root.dataset['contrast'] = resolved.contrast;

  // Drives `rem` throughout, so spacing scales with text rather than against it.
  root.style.fontSize = `${resolved.textScale * 100}%`;

  // Tells form controls, scrollbars and the like which way round they are.
  root.style.colorScheme = resolved.theme;
}
